import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { FormActions, PageHead, PageIdentity, PagesStrip, PageSection } from '../_shared/components';
import { toFormValues, toWireFields } from '../_shared/contact-fields';
import { sharedCopy } from '../_shared/copy';
import { pagesErrors } from '../_shared/errors';
import { useUpdateInformationalPage } from '../_shared/mutations';
import { informationalPageKey, useInformationalPage } from '../_shared/queries';

import { ContactChannelGrid } from './Contact.ChannelGrid';
import { ContactHandleRepeater } from './Contact.HandleRepeater';
import { ContactSkeleton } from './Contact.Skeleton';
import { copy } from './copy';
import { contactSchema, type ContactValues } from './forms.schema';

/**
 * `/pages/contact` — the operator side of `[x]` §20.17 over `[x]` §21.2: the
 * Contact Us lede plus the five channels the row enumerates.
 *
 * **This page carries no block editor, and that is the renderer's shape rather
 * than a smaller ambition.** The public About page draws a body container; the
 * public Contact page draws a head, a channels card, and the ticket form, and no
 * body container at all. A block editor here would let an operator author
 * content the public renderer has nowhere to put.
 *
 * What the composition deliberately does not draw: **the support-ticket form and
 * its reason list** (§21.3 puts the form on the public page and its reasons are
 * a configured value, not page content — the notice says so and points at the
 * queue the tickets actually land in), **a visibility control** (no `[x]` row
 * grants one), **business hours, a second number, a messaging-app entry or a
 * callback request** (the row enumerates five channels and none of these is
 * among them), **a map embed or a rendered preview**, and **a platform
 * vocabulary for the handles** (no row declares one).
 */
export function ContactPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const liveRegion = useRef<HTMLParagraphElement>(null);

  const page = useInformationalPage('contact');
  const save = useUpdateInformationalPage('contact');

  const [failure, setFailure] = useState<string | null>(null);

  const record = page.data;

  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: toFormValues(null),
  });

  // The record arrives after first paint on a cold load; a channel written from
  // another session must not be overwritten by a stale default.
  //
  // **THIS EFFECT NO LONGER TOUCHES THE SAVE CONFIRMATION, AND THAT IS THE FIX.**
  // `record.bodyFields` is a fresh object on every refetch whose data actually
  // changed — TanStack's structural sharing only preserves the reference when the
  // payload is deeply equal. A successful save invalidates this very query, the
  // refetch returns the newly-written channels, the reference moves, this effect
  // re-runs, and it used to call `setSaved(false)` — so the confirmation was torn
  // down between 47 ms and 80 ms after it appeared, measured on develop. Saving
  // the *same* values twice left the reference untouched and the message stood,
  // which is exactly the «first time it flashes, second time it is fine» the
  // report described. The confirmation is a toast now and lives outside this
  // subtree entirely, so a refetch cannot reach it.
  const { reset } = form;
  useEffect(() => {
    reset(toFormValues(record?.bodyFields));
    setFailure(null);
  }, [record?.id, record?.bodyFields, reset]);

  function announce(message: string) {
    if (liveRegion.current) liveRegion.current.textContent = message;
  }

  function restore() {
    reset(toFormValues(record?.bodyFields));
    // A failure standing over restored fields says something that is no longer
    // true.
    setFailure(null);
  }

  function onSubmit(values: ContactValues) {
    setFailure(null);

    save.mutate(
      { bodyFields: toWireFields(values) },
      {
        onSuccess: () => toast.show(copy.saved.body, 'success'),
        onError: (error) => {
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          setFailure(resolveErrorCopy(code, status, pagesErrors).message);
        },
      },
    );
  }

  if (page.isError) {
    return (
      <>
        <PageHead crumb={copy.crumb} title={copy.head.title} lede={copy.head.lede} />
        <PagesStrip current="contact" />
        <SurfaceError
          error={page.error}
          catalog={pagesErrors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: informationalPageKey('contact') })}
        />
      </>
    );
  }

  return (
    <>
      <PageHead crumb={copy.crumb} title={copy.head.title} lede={copy.head.lede} />
      <PagesStrip current="contact" />

      <p ref={liveRegion} className="sr-only" aria-live="polite" />

      {page.isPending ? (
        <ContactSkeleton />
      ) : (
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          {/*
            THE REFUSAL SITS ABOVE THE FORM, NOT UNDER IT.

            It used to be the last element on a page several screens tall, so the
            one message an operator has to act on was the one furthest from the
            control that produced it. `<Alert kind="danger">` sets `role="alert"`
            itself — this wrapper is spacing only, and a second alert region
            around it was announced twice and drew two identical banners.

            It is NOT a toast: a failure the operator has to correct must not
            disappear on a timer while they are reading it. The success
            confirmation is the toast, because nothing follows from it.
          */}
          {failure ? (
            <div className="mb-6">
              <Alert kind="danger">{failure}</Alert>
            </div>
          ) : null}

          <PageSection id="pg-identity" heading={sharedCopy.identity.heading}>
            <PageIdentity
              slug="contact"
              title={record?.title ?? copy.head.title}
              bodyShape={copy.identity.bodyShape}
            />
            <p className="mb-0 mt-4 text-xs text-ink-muted">{copy.identity.note}</p>
          </PageSection>

          <PageSection id="pg-channels" heading={copy.channels.heading}>
            <p className="mb-4 mt-0 text-xs text-ink-muted">{copy.channels.note}</p>

            <ContactChannelGrid form={form} />
            <ContactHandleRepeater form={form} announce={announce} />
          </PageSection>

          <PageSection id="pg-not-here" heading={copy.notHere.heading}>
            <Alert kind="info" title={copy.notHere.title}>
              {copy.notHere.bodyLead}
              <Link to="/cases">{copy.notHere.link}</Link>
              {copy.notHere.bodyTail}
            </Alert>
          </PageSection>

          <FormActions
            saving={save.isPending}
            onReset={restore}
            saveLabel={sharedCopy.actions.save}
            resetLabel={sharedCopy.actions.reset}
          />
        </form>
      )}
    </>
  );
}
